import React, { useState } from "react"
import { Table, Tooltip } from "antd"
import { ColumnsType } from "antd/es/table"

import { useDataImport } from "features/DataImport/api/useDataImport"
import DataImport from "../DataImport.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { getSkeletonColumns, getSkeletonData } from "shared/ui/LoadingSkeleton/LoadingSkeleton.component"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"

import { useProjectStore } from "stores/projectStore"
import handleSequenceError from "utils/handleSequenceError"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons"
import {
  DATAFILE_TYPE,
  DataFileItem,
  GenerateMapValues,
  GenerateMapResponse,
} from "types/dataImport.types"
import { EXPORT_VARIABLE_TYPES, VARIABLE_STYLE } from "types/engineExport.types"
import { UI_INPUT_TYPES } from "types/ui.types"

interface DataImportTableProps {
  dataFiles: DataFileItem[]
  isLoading: boolean
  refresh: () => Promise<void> | void
}

/**
 * DataImportTable component renders a table displaying data import status and actions
 * for a given project. It allows users to delete uploaded files or upload missing files.
 *
 * @component
 * @param props - The component props.
 * @returns The rendered DataImportTable component.
 */
const DataImportTable: React.FC<DataImportTableProps> = ({
  dataFiles,
  isLoading,
  refresh,
}) => {
  const { deleteDataImportFile, generateMapFile } = useDataImport()
  const [isGenerateDatamapModalVisible, setGeneratetDatamapModalVisible] =
    useState<boolean>(false)
  const project = useProjectStore((state) => state.project)

  /**
   * Generates a map file for the current project.
   * It triggers the generateMapFile function and displays a success message on completion.
   * In case of an error, it displays an error notification.
   *
   * @async
   * @function
   * @param additionalValues - The additional values to pass to the API.
   * @returns {Promise<void>}
   * @throws Will throw an error if the request fails.
   */
  const generateMap = async (
    additionalValues: GenerateMapValues
  ): Promise<void> => {
    try {
      const res = (await generateMapFile(
        project.id,
        additionalValues.groupSections,
        additionalValues.variableNames,
        additionalValues.variableStyle,
        additionalValues.includeQuestionText
      )) as GenerateMapResponse

      if (res.data.result.message === "File successfully saved.") {
        notify.toast.success("Map generated successfully")
        refresh()
        setGeneratetDatamapModalVisible(false)
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred"
      notify.error("Error generating map", errorMessage)
    }
  }

  /**
   * Wrapper function to match ExportModal's expected signature
   */
  const handleGenerateMap = async (
    _filename: string, // unused since noFilename=true
    additionalValues: Record<string, unknown>
  ): Promise<void> => {
    await generateMap(additionalValues as GenerateMapValues)
  }

  /**
   * Deletes a data file of the specified type.
   *
   * @async
   * @function
   * @param type - The type of file to delete.
   * @returns {Promise<void>}
   */
  const deleteFile = async (type: DATAFILE_TYPE): Promise<void> => {
    try {
      const res = await deleteDataImportFile(
        project.id,
        project.Created_by,
        type
      )
      if (handleSequenceError(res.data, "Error Deleting Data File!")) {
        notify.toast.success("Data File Deleted Successfully!")
        refresh()
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Error deleting data file!", errorMessage)
    }
  }

  const columns: ColumnsType<DataFileItem> = [
    {
      title: "File Type",
      dataIndex: "fileType",
      key: "fileType",
      width: 100,
    },
    {
      title: "Uploaded",
      dataIndex: "isUploaded",
      key: "isUploaded",
      width: 50,
      render: (_value: boolean, record: DataFileItem) => (
        <div>
          <p>{record.isUploaded ? "Yes" : "No"}</p>
        </div>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 100,
      render: (_value: undefined, record: DataFileItem) => (
        <div>
          {record.isUploaded ? (
            <DataQButton onClick={() => deleteFile(record.type)} type="danger">
              Delete
            </DataQButton>
          ) : (
            <>
              <DataImport
                name={`Upload ${record.fileType}`}
                refresh={refresh}
                type={record.type}
              />
              {record.type === DATAFILE_TYPE.MAP && (
                <>
                  <DataQButton
                    type="primary"
                    className="ml-2"
                    onClick={() => setGeneratetDatamapModalVisible(true)}
                  >
                    Generate Map
                  </DataQButton>
                  <Tooltip title="If this project has a questionnaire file you can use 'Generate Map' to directly generate the datamap or you can upload one directly using 'Upload Map File'">
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="ml-2 text-yellow-400 cursor-pointer"
                    />
                  </Tooltip>
                </>
              )}
            </>
          )}
        </div>
      ),
    },
  ]

  // Create skeleton columns and data using utility functions
  const skeletonColumns = getSkeletonColumns<DataFileItem>(columns)
  const skeletonData = getSkeletonData<DataFileItem>(3)

  return (
    <>
      <ExportModal
        title="Generate Data Map"
        confirm={handleGenerateMap}
        cancel={setGeneratetDatamapModalVisible}
        isVisible={isGenerateDatamapModalVisible}
        message={""}
        noFilename={true}
        additionalInputs={[
          {
            name: "groupSections",
            type: UI_INPUT_TYPES.CHECKBOX,
            checkboxLabel: "Group Questionnaire Sections",
            required: false,
          },
          {
            name: "variableNames",
            type: UI_INPUT_TYPES.SELECT,
            label: "Use as variable names",
            multiple: false,
            defaultValue: EXPORT_VARIABLE_TYPES.NUMBERING,
            options: [
              {
                value: EXPORT_VARIABLE_TYPES.NUMBERING,
                label: "Numbered automatically",
              },
              {
                value: EXPORT_VARIABLE_TYPES.LABELS,
                label: "Generate from questionnaire labels",
              },
            ],
            required: false,
            disabled: (values: Record<string, unknown>) =>
              values.template === true,
          },
          {
            name: "variableStyle",
            type: UI_INPUT_TYPES.SELECT,
            label: "Variable style",
            multiple: false,
            defaultValue: VARIABLE_STYLE.DEFAULT,
            options: [
              {
                value: VARIABLE_STYLE.DEFAULT,
                label: "Default (s/p prefixes)",
              },
              {
                value: VARIABLE_STYLE.DECIPHER,
                label: "Decipher standard (r/c prefixes)",
              },
            ],
            required: false,
          },
          {
            name: "includeQuestionText",
            type: UI_INPUT_TYPES.CHECKBOX,
            checkboxLabel: "Include full question text",
            required: false,
          },
        ]}
      />
      <Table<DataFileItem>
        size="small"
        className="w-full"
        rowKey="key"
        dataSource={isLoading ? skeletonData : dataFiles || []}
        columns={isLoading ? skeletonColumns : columns}
        pagination={false}
        locale={{
          emptyText: "No data",
        }}
      />
    </>
  )
}

export default DataImportTable
